import React, { useCallback, useEffect, useState } from "react";
import { adminApi } from "@/lib/adminApi";
import { API } from "@/lib/adminApi";
import { AdminPageHeader, Card, Input, Btn, Spinner, ErrorBanner, Pagination, EmptyState } from "./_shared";
import { Download, Search, Trash2, Mail } from "lucide-react";

export const Newsletter = () => {
  const [q, setQ] = useState("");
  const [skip, setSkip] = useState(0);
  const limit = 50;
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [actingEmail, setActingEmail] = useState(null);

  const load = useCallback(async () => {
    setErr(null);
    try {
      const params = { skip, limit };
      if (q) params.q = q;
      const r = await adminApi.get("/admin/newsletter", { params });
      setData(r.data);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Failed to load subscribers.");
    }
  }, [q, skip]);

  useEffect(() => { load(); }, [load]);

  const handleDelete = async (email) => {
    if (!window.confirm(`Remove ${email} from the newsletter list?`)) return;
    setActingEmail(email);
    try {
      await adminApi.delete(`/admin/newsletter/${encodeURIComponent(email)}`);
      await load();
    } catch (e) {
      setErr(e?.response?.data?.detail || "Delete failed.");
    } finally {
      setActingEmail(null);
    }
  };

  const exportHref = `${API}/admin/newsletter/export`;

  return (
    <div className="space-y-5" data-testid="newsletter-page">
      <AdminPageHeader
        eyebrow="NEWSLETTER"
        title="Email subscribers"
        sub="Everyone who opted in via the announcement waitlist or any future newsletter form. Export to CSV for use in your newsletter tool."
        action={
          <a
            href={exportHref}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-2 uppercase font-extrabold tracking-[0.2em] disabled:opacity-50 hover:opacity-90 transition-opacity"
            style={{ background: "#FF9933", color: "#0A0A0A", border: "2px solid #FF9933", padding: "0.55rem 0.9rem", fontSize: "0.7rem", letterSpacing: "0.2em" }}
            data-testid="newsletter-export"
          >
            <Download size={14} strokeWidth={3} /> Export CSV
          </a>
        }
      />

      <Card>
        <div className="flex flex-col md:flex-row md:items-end gap-3">
          <div className="flex-1">
            <label className="block text-[10px] uppercase font-bold tracking-[0.3em] text-neutral-400 mb-2">Search</label>
            <div className="relative">
              <Input
                value={q}
                onChange={(e) => { setSkip(0); setQ(e.target.value); }}
                placeholder="Search by email…"
                data-testid="newsletter-search"
                className="pl-9"
              />
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-500" strokeWidth={3} />
            </div>
          </div>
          <div className="text-xs uppercase font-bold tracking-[0.3em] text-neutral-400 md:pb-2.5" data-testid="newsletter-total">
            Total: <span style={{ color: "#FF9933" }}>{(data?.total ?? 0).toLocaleString()}</span>
          </div>
        </div>
      </Card>

      {err && <ErrorBanner message={err} />}
      {!data && !err && <Spinner />}

      {data && data.subscribers.length === 0 && (
        <EmptyState
          title={q ? "No matching subscribers." : "No subscribers yet."}
          sub={q ? "Try clearing the search." : "Opt-ins from the public site will land here."}
        />
      )}

      {data && data.subscribers.length > 0 && (
        <Card>
          <ul className="divide-y divide-[#1f1f1f]" data-testid="newsletter-list">
            {data.subscribers.map((s) => (
              <li
                key={s.email}
                className="py-3 flex items-center justify-between gap-3"
                data-testid={`subscriber-${s.email}`}
              >
                <div className="flex items-center gap-3 min-w-0">
                  <Mail size={14} strokeWidth={3} style={{ color: "#FF9933" }} className="shrink-0" />
                  <div className="min-w-0">
                    <p className="text-sm font-medium truncate" data-testid={`subscriber-email-${s.email}`}>{s.email}</p>
                    <p className="text-[10px] uppercase font-bold tracking-[0.3em] text-neutral-500 mt-0.5">
                      Joined {s.created_at?.slice(0, 16).replace("T", " ")}
                      {s.ip && <> · {s.ip}</>}
                    </p>
                  </div>
                </div>
                <Btn
                  variant="ghost"
                  onClick={() => handleDelete(s.email)}
                  disabled={actingEmail === s.email}
                  data-testid={`subscriber-delete-${s.email}`}
                >
                  <Trash2 size={12} strokeWidth={3} /> Remove
                </Btn>
              </li>
            ))}
          </ul>
          <Pagination skip={skip} limit={limit} total={data.total || 0} onChange={setSkip} />
        </Card>
      )}
    </div>
  );
};

export default Newsletter;
